/* eslint-disable react-hooks/exhaustive-deps */
import { DownloadOutlined } from '@ant-design/icons';
import { Button, message, Modal } from 'antd';
import moment from 'moment';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import getExportDataSolicitudes from '../functions/exportar';
import {
  getColumnDateRangeSearchProps,
  getColumnDateSearchProps,
  getColumnNumericSearchProps,
  getColumnSearchEqProps,
  getColumnSearchProps,
  getColumnSortBaseProps
} from '../functions/filters';
import { DEFAULT_PAGE_SIZE } from '../utils';
import { instanceRef } from '../utils/axios';
import { toRequestParams } from '../utils/filter-adapter';
import { fieldTypes } from '../utils/filter-utils';
import DataTables from './Table';

// const { RangePicker } = ;

export const URL_SOLICITUD = '/solicitud';

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

function TableAndFilters(
  { extra, withGeneralFilter, title, onRow, rowClassName, ...props },
  ref
) {
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const DEFAULT_PAGE_CONFIG = {
    showTotal: (total, range) => {
      return `${range[0]} al ${range[1]} de ${total}`;
    },
    onChange: (_, sizeParam) => {
      setPageSize(sizeParam);
    },
    showSizeChanger: true,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    pageSize: DEFAULT_PAGE_SIZE,
    current: 1
  };
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [params, setParams] = useState({});
  const [defaulSorter, setDefaulSorter] = useState();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [pagination, setPagination] = useState(DEFAULT_PAGE_CONFIG);

  const [columns, setColumns] = useState([]);
  const [columnsMap] = useState({});
  const columnsLengthPrevious = usePrevious(columns.length);
  const extras = extra || [];
  const [dataTableValues, setDataTableValues] = useState({
    paginationParam: { ...DEFAULT_PAGE_CONFIG },
    filters: {},
    sorter: null
  });

  useImperativeHandle(ref, () => ({
    reload() {
      const { paginationParam, filters, sorter } = dataTableValues;
      reload(
        {
          paginationParam,
          pageSize,
          current: 1
        },
        filters,
        sorter
      );
    },
    clear() {
      setData([]);
      setPagination(DEFAULT_PAGE_CONFIG);
    }
  }));

  const addFiltersToColumns = useCallback(() => {
    const columnasAddFilters = [];
    props.columns.forEach((column) => {
      let columnProcessed;
      if (column.filterType || column.sortable) {
        if (column.filterType === fieldTypes.DATE) {
          columnProcessed = {
            width: '120px',
            render: (fecha) => {
              return fecha ? moment(fecha).format('DD-MM-YYYY') : 'Sin fecha';
            },
            ...getColumnDateSearchProps(column),
            ...column
          };
        } else if (column.filterType === fieldTypes.DATE_RANGE) {
          columnProcessed = {
            width: '120px',
            render: (fecha) => {
              return fecha ? moment(fecha).format('DD-MM-YYYY') : 'Sin fecha';
            },
            ...getColumnDateRangeSearchProps(column),
            ...column
          };
        }
        if (column.filterType === fieldTypes.DATETIME) {
          columnProcessed = {
            width: '120px',
            render: (fecha) => {
              return fecha
                ? moment(fecha).format('DD-MM-YYYY HH:mm')
                : 'Sin fecha';
            },
            ...getColumnDateSearchProps(column),
            ...column
          };
        } else if (column.filterType === fieldTypes.ENUM) {
          columnProcessed = {
            ...getColumnSearchEqProps(column.sortable),
            ...column
          };
        } else if (column.filterType === fieldTypes.STRING) {
          columnProcessed = {
            ...getColumnSearchProps(column),
            ...column
          };
        } else if (column.filterType === fieldTypes.NUMERIC) {
          columnProcessed = {
            ...getColumnNumericSearchProps(column),
            ...column,
            align: 'right'
          };
        } else if (!column.filterType && column.sortable) {
          // only sortable
          columnProcessed = {
            ...getColumnSortBaseProps(),
            ...column
          };
        }
      } else {
        columnProcessed = {
          ...column
        };
      }
      const { key } = column;
      const columnKey =
        key ||
        (Array.isArray(column.dataIndex)
          ? column.dataIndex.join('.')
          : column.dataIndex);

      columnsMap[columnKey] = columnProcessed;

      if (column.defaultSortOrder && column.sortable) {
        setDefaulSorter({
          column: columnProcessed,
          field: columnKey,
          order: column.defaultSortOrder
        });
      }

      columnasAddFilters.push(columnProcessed);
    });

    return columnasAddFilters;
    // eslint-disable-next-line react/destructuring-assignment
  }, [props.columns]);

  const getColumnsMapDefaultFilters = () => {
    const keys = Object.keys(props.defaultFilters);
    const filtro = {};
    const columnsMapNuevo = {};
    keys.forEach((e) => {
      columnsMapNuevo[e] = {
        filterType: props.defaultFilters[e].filterType,
        filterCondition: props.defaultFilters[e].filterCondition
      };
      filtro[e] = props.defaultFilters[e].value;
    });
    return { filtro, columnsMapNuevo };
  };

  const reload = (paginationParam, filters, sorter) => {
    let customFilters;
    if (props.customFilters) {
      customFilters = props.customFilters(filters);
    }
    const finalFilters = { ...(customFilters || filters) };
    const columnsMapNuevo = { ...columnsMap };
    if (props.defaultFilters) {
      const filtroAndMap = getColumnsMapDefaultFilters();
      const keysNuevos = Object.keys(filtroAndMap.columnsMapNuevo);
      const keysFinales = Object.keys(finalFilters);
      for (let j = 0; j < keysNuevos.length; j += 1) {
        const key = keysNuevos[j];
        // Si se aplica un filtro de columna debe prevalecer
        if (keysFinales.includes(key)) {
          // Si al borrar el filtro y queda la propiedad pero con valor null o undefined se debe aplicar el filtro externo
          if (!finalFilters[key]) {
            columnsMapNuevo[key] = filtroAndMap.columnsMapNuevo[key];
            finalFilters[key] = filtroAndMap.filtro[key];
          }
        } else {
          // si no se aplica un filtro de columna se puede aplicar el filtro externo
          columnsMapNuevo[key] = filtroAndMap.columnsMapNuevo[key];
          finalFilters[key] = filtroAndMap.filtro[key];
        }
      }
    }
    const paramsProccess = toRequestParams(
      finalFilters,
      paginationParam,
      sorter,
      columnsMapNuevo
    );
    setParams(paramsProccess);
    fetch(paramsProccess, paginationParam);
  };

  const handleTableChange = (paginationParam, filters, sorter) => {
    setDataTableValues({ paginationParam, filters, sorter });
    reload(paginationParam, filters, sorter);
  };
  const fetch = async (paramsProccess, paginationParam) => {
    try {
      setLoading(true);
      try {
        const response = await instanceRef.instance.get(props.url, {
          params: paramsProccess || params
        });
        let finalCount = response.data.data.count;
        if (
          response.data.data.count &&
          Array.isArray(response.data.data.count)
        ) {
          finalCount = response.data.data.count.length;
        }
        setData({ ...response.data.data, count: finalCount });
        setPagination({
          ...DEFAULT_PAGE_CONFIG,
          ...paginationParam,
          total: finalCount
        });
      } catch (error) {
        // error
      }
    } catch (error) {
      // error
    }
    setLoading(false);
  };

  const getExportData = async () => {
    if (props.getExportData) {
      await props.getExportData(params);
      return;
    }
    try {
      const response = await instanceRef.instance.get(props.url, {
        params
      });
      if (response.data.data.rows.length > 0) {
        if (props.url === URL_SOLICITUD) {
          getExportDataSolicitudes(response.data.data.rows);
        }
      } else {
        message.info('No se encontraron datos');
      }
    } catch (error) {
      // error
    }
  };

  const handleOk = () => {
    setIsModalVisible(false);
    getExportData();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  // const handleRangeChange = (dates, dateStrings) => {
  //   setRange({ desde: dateStrings[0], hasta: dateStrings[1] });
  // };

  const hasExportar = () => {
    if (props.hasExportar) {
      return [
        <Button
          type="primary"
          shape="round"
          key="export"
          icon={<DownloadOutlined />}
          size="middle"
          onClick={getExportData}
          style={{ marginLeft: 10 }}
        >
          Exportar Datos
        </Button>
      ];
    }
    return [];
  };

  useEffect(() => {
    let filtro = {};
    let columnsMapNuevo = {};
    if (props.defaultFilters) {
      const filtroAndMap = getColumnsMapDefaultFilters();
      filtro = filtroAndMap.filtro;
      columnsMapNuevo = filtroAndMap.columnsMapNuevo;
    }
    if (columns.length) {
      const defaulFilters = filtro || null;
      const paramsProccess = toRequestParams(
        defaulFilters,
        pagination,
        defaulSorter,
        columnsMapNuevo
      );
      setParams(paramsProccess);
      if (columnsLengthPrevious !== columns.length) {
        fetch(paramsProccess);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  useEffect(() => {
    setColumns(addFiltersToColumns());
    // eslint-disable-next-line react/destructuring-assignment
  }, [props.columns, addFiltersToColumns]);

  return (
    <>
      <DataTables
        size="small"
        bordered
        ref={ref}
        withGeneralFilter={withGeneralFilter}
        title={title}
        columns={columns}
        rowKey={(record) => (props.rowKey ? props.rowKey(record) : record.id)}
        dataSource={data && data.rows}
        pagination={pagination}
        loading={loading}
        handleTableChange={handleTableChange}
        extra={[...extras, ...hasExportar()]}
        expandable={props.expandable}
        tableLayout={props.tableLayout}
        placeholder={props.placeholder}
        rowSelection={props.rowSelection}
        locale={props.locale}
        onRow={onRow}
        rowClassName={`table-small ${rowClassName}`}
      />
      <Modal
        title="Exportar Datos"
        open={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
      >
        <p>Exportar todas las páginas o solo la página actual:</p>
        {/* <RangePicker size="middle" onChange={handleRangeChange} /> */}
      </Modal>
    </>
  );
}

export default forwardRef(TableAndFilters);
